<template>
  <div :class="{ 'vsm-standalone': standalone }" style="margin:20px">
    <FiPageRail v-if="!standalone" class="vsm-rail" :title="isMobile() ? 'VSM' : 'Vehicle Status Manager'" :class="{ 'vsm-upcoming-rail--mobile': isMobile() }">
      <template #actions>
        <b-button
          v-if="!cardView"
          size="sm"
          class="vst-auto-btn"
          :class="{ 'vst-auto-btn--on': auto_refresh }"
          variant="outline-secondary"
          :title="autoRefreshTitle"
          :aria-pressed="auto_refresh ? 'true' : 'false'"
          aria-label="Toggle auto-refresh"
          @click="toggleAutoRefresh"
        ><span class="vst-auto-dot" :class="{ 'vst-auto-dot--live': auto_refresh }" aria-hidden="true"></span>Auto</b-button>
        <b-button v-if="!cardView" size="sm" variant="primary" title="Download CSV" aria-label="download" @click="downloadCsv()"><i class="fa fa-download"></i></b-button>
        <b-button variant="success" size="sm" @click="view_more_info = !view_more_info" aria-label="more info">
          <i class="fa fa-info-circle"></i>
        </b-button>
      </template>
    </FiPageRail>
    <div v-if="standalone" class="vsm-standalone-header" style="min-width: 750px; font-size: 24px; margin-bottom: 10px;">
      <a target="_new" href="https://www.initekconsulting.com/fleet-integrations"><img style="margin-top:-5px" width="40" src="favicon.ico"/></a>&nbsp;Vehicle Status Manager&nbsp;- {{summary.tenant_name}} {{selected_base}}
      <b-spinner v-if="reloading" style="width: 1.5rem; height: 1.5rem"></b-spinner><span v-else style="width: 1.5rem; height: 1.5rem">&nbsp;</span>
      &nbsp;<vsm-reload-button @click="load_data()" />
    </div>

    <div v-if="!loading && summary && (!standalone || !driver_mode)">
      <vsm-mobile-filter-chips
        :size="isMobile() ? 'sm' : 'md'"
        :total-format="isMobile() || standalone ? 'slash' : 'of'"
        :items-count="items.length"
        :total-count="summary.total_cnt"
        :total-selected="selected_vehicle_statuses.length === 0"
        :show-search="false"
        @clear-filters="filter_by_status(null)"
      >
        <span class="vsm-chip vsm-chip-clickable available" :class="{ 'vsm-chip-selected': selected_vehicle_statuses.includes('available') }" @click="filter_by_status('available')">Available ({{ available_cnt }})</span>
        <span v-if="!isMobile()" class="vsm-chip vsm-chip-label">Wash</span>
        <span class="vsm-chip vsm-chip-clickable cleaned" :class="{ 'vsm-chip-selected': selected_vehicle_statuses.includes('cleaned') }" @click="filter_by_status('cleaned')">Clean ({{ clean_cnt }})</span>
        <span class="vsm-chip vsm-chip-clickable dirty" :class="{ 'vsm-chip-selected': selected_vehicle_statuses.includes('dirty') }" @click="filter_by_status('dirty')">Dirty ({{ dirty_cnt }})</span>
        <span class="vsm-chip vsm-chip-clickable out" :class="{ 'vsm-chip-selected': selected_vehicle_statuses.includes('out') }" @click="filter_by_status('out')">Out ({{ out_cnt }})</span>
        <span v-if="!isMobile()" class="vsm-chip vsm-chip-label">Maint</span>
        <span class="vsm-chip vsm-chip-clickable ready" :class="{ 'vsm-chip-selected': selected_maintenance_statuses.includes('ready') }" @click="filter_by_maint_status('ready')">Ready ({{ ready_cnt }})</span>
        <span class="vsm-chip vsm-chip-clickable pm" :class="{ 'vsm-chip-selected': selected_maintenance_statuses.includes('pm') }" @click="filter_by_maint_status('pm')">PM ({{ pm_cnt }})</span>
        <span class="vsm-chip vsm-chip-clickable oos" :class="{ 'vsm-chip-selected': selected_maintenance_statuses.includes('oos') }" @click="filter_by_maint_status('oos')">OOS ({{ oos_cnt }})</span>
        <template #extras>
          <span v-if="!isMobile() && !standalone" class="vsm-filter-extras">
            <b-form-select v-if="base_options && base_options.length > 2" v-model="selected_base" :options="base_options" class="vsm-filter-select"/>
            <span v-if="summary.spare_notes_hash" class="d-flex align-items-center"><b>Spares&nbsp;</b><b-form-input style="width:90px" v-model="summary.spare_notes" v-on:focus="onSpareFocus()" v-on:blur="save_event_notes()" @keydown.enter.native="save_event_notes()"/></span>
          </span>
        </template>
        <template #actions>
          <vsm-status-row-actions v-if="!standalone" @reload="load_data()" />
        </template>
      </vsm-mobile-filter-chips>
    </div>

    <b-modal title="More Info" size="xl" v-model="view_more_info" ok-only scrollable dialog-class="vsm-info-dialog" body-class="vsm-info-body">
      <h4>Here are details on the filters and statuses used for the VSM</h4>
      <h4>Special Filters</h4>
      <div class="vsm-info-line"><b>AVAILABLE</b> &mdash; vehicles in CLEAN wash status and READY maintenance status</div>
      <hr/>
      <h4>Wash Status</h4>
      <div class="vsm-info-line"><b>CLEAN</b> &mdash; indicates that the vehicle is clean & ready for service (manually managed)</div>
      <div class="vsm-info-line"><b>DIRTY</b> &mdash; automatically set by the system when a vehicle arrives back to the finish location or yard</div>
      <div class="vsm-info-line"><b>OUT</b> &mdash; automatically set by the system when a vehicle leaves for a planned booking</div>
      <hr/>
      <h4>Maintenance Status</h4>
      <div class="vsm-info-line"><b>READY</b> &mdash; indicates the vehicle is maintenance ready (automatically set by the system when all PMs are cleared) unless manually set to PM or OOS status</div>
      <div class="vsm-info-line"><b>PM</b> &mdash; planned / preventative maintenance (manually managed, optionally synced with maintenance platform)</div>
      <div class="vsm-info-line"><b>OOS</b> &mdash; out of service (manually managed, optionally synced with maintenance platform)</div>
      <hr/>
      <h4>VSM Standalone Mode</h4>
      <div class="vsm-info-line">provides a readonly view that refreshes every minute to keep all teams in sync</div>
      <div class="vsm-info-line"><a target="_blank" :href="'./#/vsm-maint-tbn/reload/' + standaloneId">click here to load the standalone VSM</a><vsm-standalone-url-copy-button :href="'./#/vsm-maint-tbn/reload/' + standaloneId" :tenant-id="tenant_id"/></div>
      <span v-if="base_options.length > 2">
        <div class="vsm-info-line">click on a base name to load the VSM specific to that base:</div>
        <div class="vsm-info-line">
          <span v-if="base.value != ''" v-for="base in base_options">
            <a target="_blank" :href="'./#/vsm-maint-tbn/reload/' + standaloneId + '/' + base.value">{{base.value}}</a><vsm-standalone-url-copy-button :href="'./#/vsm-maint-tbn/reload/' + standaloneId + '/' + base.value" :tenant-id="tenant_id"/>&nbsp;|&nbsp;
          </span>
        </div>
      </span>
      <div class="vsm-info-line"><a target="_blank" :href="'./#/vsm-maint-tbn/reloadreturn/' + standaloneId">click here to load the standalone Wash VSM (by return time)</a><vsm-standalone-url-copy-button :href="'./#/vsm-maint-tbn/reloadreturn/' + standaloneId" :tenant-id="tenant_id"/></div>
      <span v-if="base_options.length > 2">
        <div class="vsm-info-line">click on a base name to load the Wash VSM (by return time) specific to that base:</div>
        <div class="vsm-info-line">
          <span v-for="base in base_options.filter(b => b.value != '')" :key="'wash-' + base.value">
            <a target="_blank" :href="'./#/vsm-maint-tbn/reloadreturn/' + standaloneId + '/' + base.value">{{base.value}}</a><vsm-standalone-url-copy-button :href="'./#/vsm-maint-tbn/reloadreturn/' + standaloneId + '/' + base.value" :tenant-id="tenant_id"/>&nbsp;|&nbsp;
          </span>
        </div>
      </span>
      <div class="vsm-info-line"><a target="_blank" :href="'./#/vsm-maint-tbn/driver/' + standaloneId">click here to load the standalone driver VSM</a><vsm-standalone-url-copy-button :href="'./#/vsm-maint-tbn/driver/' + standaloneId" :tenant-id="tenant_id"/></div>
      <span v-if="base_options.length > 2">
        <div class="vsm-info-line">click on a base name to load the driver VSM specific to that base:</div>
        <div class="vsm-info-line">
          <span v-if="base.value != ''" v-for="base in base_options">
            <a target="_blank" :href="'./#/vsm-maint-tbn/driver/' + standaloneId + '/' + base.value">{{base.value}}</a><vsm-standalone-url-copy-button :href="'./#/vsm-maint-tbn/driver/' + standaloneId + '/' + base.value" :tenant-id="tenant_id"/>&nbsp;|&nbsp;
          </span>
        </div>
      </span>
      <hr/>
      <span v-if="summary.aws_phone_number">
        <h4>Update Status & Notes Over SMS</h4>
        <div class="vsm-info-line">simply send a text in one of the following formats to #{{summary.aws_phone_number | prettyPhoneNumber}}</div>
        <div class="vsm-info-sub">update the status only, send '[status] [vehicle name]' - for example 'cleaned 123'</div>
        <div class="vsm-info-sub">to update the notes only, send 'notes [vehicle name], [notes]' - for example 'notes 123, issue found'</div>
        <div class="vsm-info-sub">update the status & notes, send '[status] [vehicle name], [notes]' - for example 'cleaned 123, issue found'</div>
        <div class="vsm-info-sub">to clear notes, send '[notes] [vehicle name]' - for example 'notes 123'</div>
        <h4>Update Numeric Fields Over SMS</h4>
        <div class="vsm-info-line">send a text in one of the following formats to #{{summary.aws_phone_number | prettyPhoneNumber}}</div>
        <div class="vsm-info-sub">'def [value] [vehicle name]' - for example 'def 13.2 123' (max 50)</div>
        <div class="vsm-info-sub">'fuel [value] [vehicle name]' - for example 'fuel 45 123' (max 500)</div>
        <div class="vsm-info-sub">'odo [value] [vehicle name]' - for example 'odo 12543 123' (max 1,500,000)</div>
        <div class="vsm-info-sub">parentheses format also supported, e.g. 'def(13.2) 123'</div>
        <div class="vsm-info-sub">commands are case-insensitive</div>
      </span>
    </b-modal>

    <!-- floating status picker (ops_vsm pattern); anchored by the mixin -->
    <vsm-status-picker-pop
      v-if="editing_cell"
      ref="pickerPop"
      :options="picker_field === 'maintenance_status' ? maintenance_status_options : status_options"
      :current="picker_item && picker_item[picker_field]"
      :chip-class="picker_field === 'maintenance_status' ? maintChipClass : washChipClass"
      :style-obj="picker_style"
      :aria-label="picker_field === 'maintenance_status' ? 'Set maintenance status' : 'Set wash status'"
      @select="setStatus(picker_item, picker_field, $event)"
      @esc="cancelEdit(true)"
    />

    <!-- phones get cards instead of the table (mirrors the VSM Upcoming pages) -->
    <vsm-mobile-cards v-if="cardView" :items="items" :fields="cardFields" :datetime-format="summary.datetime_format">
      <template #controls="{ item }">
        <b-form-select v-if="!standalone && !(user.role=='dispatch' || user.role=='garage')" class="vsm-up-chip-select" :class="item.vehicle_status ? 'vsm-up-cell-' + item.vehicle_status.toLowerCase() : null" v-model="item.vehicle_status" :options="status_options" @change="update_vehicle_status({}, item)"/>
        <span v-else-if="item.vehicle_status" class="vsm-up-mini-chip" :class="'vsm-up-cell-' + item.vehicle_status.toLowerCase()">{{ item.vehicle_status }}</span>
        <b-form-select v-if="!standalone && !(user.role=='dispatch' || user.role=='wash-bay')" class="vsm-up-chip-select" :class="item.maintenance_status ? 'vsm-up-cell-maint-' + item.maintenance_status.toLowerCase() : null" v-model="item.maintenance_status" :options="maintenance_status_options" @change="update_maint_status({}, item)"/>
        <span v-else-if="item.maintenance_status" class="vsm-up-mini-chip" :class="'vsm-up-cell-maint-' + item.maintenance_status.toLowerCase()">{{ display_maint_status(item.maintenance_status) }}</span>
      </template>
    </vsm-mobile-cards>
    <b-table v-else :busy="loading" :sticky-header="!standalone ? '70vh' : false" bordered :items="items" :fields="visibleFields" small
             :table-class="['vsm-status-table', rowColorsOff ? 'vsm-rowcolors-off' : null, isMobile() ? 'vsm-mobile-table' : null]"
             :current-page="currentPage" :tbody-tr-class="get_row_color" :style="'font-size:' + summary.font_size + 'px'"  :sort-compare="tableSortCompare"
             :per-page="perPage">

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner class="align-middle"></b-spinner>
        </div>
      </template>

      <template slot="vehicle_status" slot-scope="row">
        <button
          v-if="!standalone && !(user.role=='dispatch' || user.role=='garage')"
          type="button"
          class="vst-chip-btn"
          title="Update wash status"
          aria-haspopup="menu"
          :aria-expanded="editing_cell === cellKey(row.item, 'vehicle_status') ? 'true' : 'false'"
          :aria-label="'Change wash status for ' + row.item.name"
          :data-status-trigger="cellKey(row.item, 'vehicle_status')"
          @click="startEdit(row.item, 'vehicle_status', $event)"
        ><span class="vst-status-chip vst-status-chip--trigger" :class="washChipClass(row.item.vehicle_status)">{{ statusLabel(row.item.vehicle_status) || '-' }}<i class="fa fa-caret-down vst-status-caret" aria-hidden="true"></i></span></button>
        <span v-else-if="rowColorsOff && row.item.vehicle_status" class="vst-status-chip" :class="washChipClass(row.item.vehicle_status)">{{row.item.vehicle_status}}</span>
            <span v-else>{{row.item.vehicle_status}}</span>
        <template v-if="isMobile()">
          <br/>
          <button
            v-if="!standalone && !(user.role=='dispatch' || user.role=='wash-bay')"
            type="button"
            class="vst-chip-btn"
            title="Update maintenance status"
            aria-haspopup="menu"
            :aria-expanded="editing_cell === cellKey(row.item, 'maintenance_status') ? 'true' : 'false'"
            :aria-label="'Change maintenance status for ' + row.item.name"
            :data-status-trigger="cellKey(row.item, 'maintenance_status')"
            @click="startEdit(row.item, 'maintenance_status', $event)"
          ><span class="vst-status-chip vst-status-chip--trigger" :class="maintChipClass(row.item.maintenance_status)">{{ statusLabel(row.item.maintenance_status) || '-' }}<i class="fa fa-caret-down vst-status-caret" aria-hidden="true"></i></span></button>
          <span v-else-if="rowColorsOff && row.item.maintenance_status" class="vst-status-chip" :class="maintChipClass(row.item.maintenance_status)">{{display_maint_status(row.item.maintenance_status)}}</span>
          <span v-else>{{display_maint_status(row.item.maintenance_status)}}</span>
        </template>
      </template>

      <template slot="maintenance_status" slot-scope="row">
        <button
          v-if="!standalone && !(user.role=='dispatch' || user.role=='wash-bay')"
          type="button"
          class="vst-chip-btn"
          title="Update maintenance status"
          aria-haspopup="menu"
          :aria-expanded="editing_cell === cellKey(row.item, 'maintenance_status') ? 'true' : 'false'"
          :aria-label="'Change maintenance status for ' + row.item.name"
          :data-status-trigger="cellKey(row.item, 'maintenance_status')"
          @click="startEdit(row.item, 'maintenance_status', $event)"
        ><span class="vst-status-chip vst-status-chip--trigger" :class="maintChipClass(row.item.maintenance_status)">{{ statusLabel(row.item.maintenance_status) || '-' }}<i class="fa fa-caret-down vst-status-caret" aria-hidden="true"></i></span></button>
        <span v-else-if="rowColorsOff && row.item.maintenance_status" class="vst-status-chip" :class="maintChipClass(row.item.maintenance_status)">{{display_maint_status(row.item.maintenance_status)}}</span>
          <span v-else>{{display_maint_status(row.item.maintenance_status)}}</span>
      </template>

      <template slot="notes" slot-scope="row">
        <div v-if="!standalone" class="d-flex align-items-center justify-content-center">
            <b-form-input :class="['vst-notes-input', { 'vsm-mobile-notes-input': isMobile(), 'vst-notes-input--saved': notes_saved === row.item.name }]" :style="isMobile() ? null : 'min-width:250px'" v-model="row.item.notes" :disabled="updating" v-on:focus="markEditing(row.item); noteFocus(row.item)" v-on:blur="update_notes(row, row.item)"/>
        </div>
        <div class="vst-notes-text" v-else>{{row.item.notes}}</div>
      </template>

      <template slot="wheelchair" slot-scope="row">
          <b-checkbox size="lg" v-model="row.item.wheelchair" @change="update_wc_after(row, row.item)" :disabled="standalone || row.item.name.indexOf('-WC') == -1"/>
      </template>

      <template slot="stayanddo" slot-scope="row">
        <b-checkbox size="lg" v-model="row.item.stayanddo" @change="update_stayanddo_after(row, row.item)" :disabled="standalone"/>
      </template>

      <template slot="next_mvmt_id" slot-scope="row">
        <router-link style="font-weight: bold;" :to="{ path: '/actuals_tbn?booking_id=' + row.item.next_mvmt_booking_hash }">{{ row.item.next_mvmt_id }}</router-link>
      </template>

      <template slot="current_mvmt_id" slot-scope="row">
        <router-link style="font-weight: bold;" :to="{ path: '/actuals_tbn?booking_id=' + row.item.current_mvmt_booking_hash }">{{ row.item.current_mvmt_id }}</router-link>
      </template>

      <template slot="next_mvmt_driver" slot-scope="row">
        {{row.item.next_mvmt_driver | trim(15)}}
        <template v-if="row.item.next_mvmt_list" v-for="next_mvmt in row.item.next_mvmt_list">
          <br/>{{next_mvmt.next_mvmt_driver | trim(15)}}
        </template>
      </template>

      <template slot="next_mvmt_start_base" slot-scope="row">
        <span v-if="row.item.next_mvmt_start_base && row.item.next_mvmt_start_base.indexOf('(') != -1">
          {{ row.item.next_mvmt_start_base | findbetween("(",")") }}
        </span>
        <span v-else>
          {{row.item.next_mvmt_start_base | trim(15)}}
        </span>
        <template v-if="row.item.next_mvmt_list" v-for="next_mvmt in row.item.next_mvmt_list">
            <br/>
            <span v-if="next_mvmt.next_mvmt_start_base && next_mvmt.next_mvmt_start_base.indexOf('(') != -1">
              {{ next_mvmt.next_mvmt_start_base | findbetween("(",")") }}
            </span>
            <span v-else>
              {{next_mvmt.next_mvmt_start_base | trim(15)}}
            </span>
        </template>
      </template>

      <template slot="next_mvmt_driver_start" slot-scope="row">
        {{row.item.next_mvmt_driver_start | convert24HrDateTimeSmall(summary.datetime_format)}}
      </template>
      
      <template slot="next_mvmt_time" slot-scope="row">
        {{row.item.next_mvmt_time | formatDateTimeCustom(summary.datetime_format)}}
        <template v-if="row.item.next_mvmt_list" v-for="next_mvmt in row.item.next_mvmt_list">
          <br/>{{next_mvmt.next_mvmt_time | formatDateTimeCustom(summary.datetime_format)}}
        </template>
      </template>

      <template slot="next_mvmt_id" slot-scope="row">
        {{row.item.next_mvmt_id | trim(15)}}
        <template v-if="row.item.next_mvmt_list" v-for="next_mvmt in row.item.next_mvmt_list">
          <br/>{{next_mvmt.next_mvmt_id | trim(15)}}
        </template>
      </template>

      <template slot="next_mvmt_delta" slot-scope="row">
          <span v-if="row.item.next_mvmt_delta" :style="row.item.next_mvmt_delta.indexOf('d') != -1 ? '' : 'color: red'">
            {{row.item.next_mvmt_delta}}
          </span>
        <template v-if="row.item.next_mvmt_list" v-for="next_mvmt in row.item.next_mvmt_list">
          <br/>{{next_mvmt.next_mvmt_delta}}
        </template>
      </template>

      <template slot="next_mvmt_days" slot-scope="row">
        {{row.item.next_mvmt_days}}
        <template v-if="!driver_mode && row.item.next_mvmt_list" v-for="next_mvmt in row.item.next_mvmt_list">
          <br/>{{next_mvmt.next_mvmt_days}}
        </template>
      </template>
      
      <template slot="tote" slot-scope="row">
        <b-checkbox v-if="row.item.tote_color" :style="'font-weight: bold; opacity: 0.7; background-color: ' + row.item.tote_color + ' !important'" size="lg" v-model="row.item.tote" @change="update_tote_after(row, row.item)" :disabled="standalone">{{row.item.tote_label}}</b-checkbox>
      </template>

      <template slot="last_gps_time" slot-scope="row">
        {{row.item.last_gps_time | formatDateTimeCustom(summary.datetime_format)}}
      </template>

      <template slot="current_mvmt_finish_base" slot-scope="row">
        <span v-if="row.item.current_mvmt_finish_base && row.item.current_mvmt_finish_base.indexOf('(') != -1">
          {{ row.item.current_mvmt_finish_base | findbetween("(",")") }}
        </span>
        <span v-else>
          {{row.item.current_mvmt_finish_base | trim(15)}}
        </span>
      </template>

      <template slot="name" slot-scope="row">
        <a class="vst-vehicle-link" target="_blank" :href="'/#/vehicle-track/' + tenant_id + '/' + row.item.name">{{row.item.name}}</a>
      </template>

      <template slot="processed_time" slot-scope="row">
        <span v-if="row.item.vehicle_info">
          {{row.item.processed_time | formatDateTimeCustom(summary.datetime_format)}}
        </span>
      </template>

      <template slot="last_washed_time" slot-scope="row">
        <span v-if="row.item.vehicle_info">
          {{row.item.last_washed_time | formatDateTimeCustom(summary.datetime_format)}}
        </span>
      </template>

      <template slot="last_dirty_time" slot-scope="row">
        <span v-if="row.item.vehicle_info">
          {{row.item.last_dirty_time | formatDateTimeCustom(summary.datetime_format)}}
        </span>
      </template>

      <template slot="last_returned_time" slot-scope="row">
        <span v-if="row.item.vehicle_info">
          {{row.item.last_returned_time | formatDateTimeCustom(summary.datetime_format)}}
        </span>
      </template>

      <template slot="current_mvmt_delta" slot-scope="row">
        <span v-if="row.item.current_mvmt_delta" :style="row.item.current_mvmt_delta.indexOf('late') == -1 ? '' : 'color: red'">
          {{row.item.current_mvmt_delta}}
        </span>
      </template>

      <template slot="last_cleaned_time" slot-scope="row">
        <span v-if="row.item.vehicle_info">
          {{row.item.last_cleaned_time | formatDateTimeCustom(summary.datetime_format)}}
        </span>
      </template>

      <template slot="last_out_time" slot-scope="row">
        <span v-if="row.item.vehicle_info">
          {{row.item.last_out_time | formatDateTimeCustom(summary.datetime_format)}}
        </span>
      </template>

      <template slot="last_maintenance_time" slot-scope="row">
        <span v-if="row.item.vehicle_info">
          {{row.item.last_maintenance_time | formatDateTimeCustom(summary.datetime_format)}}
        </span>
      </template>

      <template slot="last_ready_time" slot-scope="row">
        <span v-if="row.item.vehicle_info">
          {{row.item.last_ready_time | formatDateTimeCustom(summary.datetime_format)}}
        </span>
      </template>

      <template slot="details" slot-scope="row">
        <button
          v-if="!(user.role == 'garage' || user.role == 'wash-bay')"
          type="button"
          class="vst-expand-btn"
          :class="{ 'vst-expand-btn--open': row.detailsShowing }"
          aria-label="Show status history"
          :aria-expanded="row.detailsShowing ? 'true' : 'false'"
          @click="get_vehicle_info_history(row.item); row.toggleDetails();"
        ><i class="fa fa-chevron-right"></i></button>
      </template>

      <template slot="row-details" slot-scope="row">
        <b-card style="text-align: left;">
            <!-- status change history: when/by plus the old -> new values that
                 changed in each update -->
            <vsm-status-history-list :history="row.item.history" :datetime-format="summary.datetime_format"/>
        </b-card>
      </template>
    </b-table>
    <b-row v-if="!standalone && !cardView">
      <b-col>
        <b-pagination
            v-model="currentPage"
            :total-rows="items.length"
            :per-page="perPage"
            align="center"
        >
        </b-pagination>
      </b-col>
    </b-row>
  </div>
</template>

<script>

import {get_vehicle_info_history, save_event_notes} from '../networking/apis'
import {mapState} from "vuex";
import moment from 'moment'
import {get_tbn_vehicle_info, tbn_update_vehicle_status} from "../networking/tbn_apis";
import {preserveActiveEdits} from '../utils/vsm_status_record_diff'
import VsmStandaloneUrlCopyButton from './VsmStandaloneUrlCopyButton.vue'
import VsmStatusRowActions from './VsmStatusRowActions.vue'
import VsmReloadButton from './VsmReloadButton.vue'
import VsmMobileFilterChips from './vsm_mobile_filter_chips.vue'
import VsmMobileCards from './vsm_mobile_cards.vue'
import VsmStatusPickerPop from './VsmStatusPickerPop.vue'
import VsmStatusHistoryList from './VsmStatusHistoryList.vue'
import { downloadVsmCsv } from '@/utils/vsm_csv'
import { displayMaintStatus, VSM_MOBILE_DEFAULT_FIELD_KEYS } from '../utils/vsm_fields'

import vsmMobileView from '@/mixins/vsm_mobile_view'
import vsmOpsTable from '@/mixins/vsm_ops_table'

export default {
  mixins: [vsmMobileView, vsmOpsTable],
  name: 'vehicle_status_maint_tbn',
  components: {
    VsmStandaloneUrlCopyButton,
    VsmStatusRowActions,
    VsmReloadButton,
    VsmMobileFilterChips,
    VsmMobileCards,
    VsmStatusPickerPop,
    VsmStatusHistoryList,
  },
  metaInfo: {
    title: "Fleet Integrations",
    titleTemplate: "Fleet Integrations",
  },
  data() {
    return {
      view_more_info: false,
      standalone: false,
      driver_mode: false,
      mode: '',
      tenant_id: null,
      updating: false,
      items_filter: null,
      columnSelection: false,
      reloading: false,
      reload_mode: 0,
      timer: null,
      items: [],
      items_full: [],
      selected_base: '',
      base_options: [],
      summary: [],
      spare_notes_baseline: '',
      spares_editing: false,
      // names of rows whose Notes field is actively being typed into -
      // protects against load_data()/reload_data() replacing items mid-edit
      // and wiping out what the user is typing before they get to save it
      active_edit_names: {},
      loading: true,
      selected_vehicle_statuses: [],
      selected_vehicle_tag: null,
      selected_maintenance_statuses: [],
      totalRows: 1,
      currentPage: 1,
      perPage: 300,
      pages: [],
      status_options: [
        { value: 'UNK', text: '' },
        { value: 'DIRTY', text: 'DIRTY' },
        { value: 'CLEANED', text: 'CLEAN' },
        { value: 'OUT', text: 'OUT' },
      ],
      maintenance_status_options: [
        { value: 'READY', text: 'READY' },
        { value: 'PM', text: 'PM' },
        { value: 'OOS', text: 'OOS' },
      ],
      fields: [
        { key: 'details', label: '', sortable: false, thStyle: { width: "30px" }},
        { key: 'name', label: 'Vehicle', sortable: true, thStyle: { width: "100px" }},
        { key: 'vehicle_status', label:'Status', sortable: true, thStyle: { width: "150px" }},
        { key: 'maintenance_status', label:'Maint Status', sortable: true, thStyle: { width: "160px" }},
        { key: 'fleetio_status', label:'Fleetio Status', sortable: true, thStyle: { width: "160px" }},
        { key: 'rta_status', label:'RTA Status', sortable: true, thStyle: { width: "160px" }},
        { key: 'rta_work_order_status', label:'RTA WO Status', sortable: true, thStyle: { width: "180px" }},

        { key: 'last_dirty_time', tdClass: 'vsm-dt-cell', label:'Dirty', sortable: true, visible: true},
        { key: 'last_cleaned_time', tdClass: 'vsm-dt-cell', label:'Cleaned', sortable: true, visible: true, thStyle: { width: "140px" }},
        { key: 'last_cleaned_number', label:'Cleaned #', sortable: true, visible: true},
        { key: 'last_washed_time', tdClass: 'vsm-dt-cell', label:'Washed', sortable: true, visible: true, thStyle: { width: "140px" }},
        { key: 'last_washed_number', label:'Washed #', sortable: true, visible: true},
        { key: 'last_returned_time', tdClass: 'vsm-dt-cell', label:'Last Return', sortable: true, thStyle: { width: "150px" }},

        { key: 'current_mvmt_finish_base', label: 'Current Finish', sortable: true, thStyle: { width: "160px" }},
        { key: 'current_mvmt_return_time', tdClass: 'vsm-dt-cell', label: 'Current Return', sortable: true, visible: true, thStyle: { width: "160px" }},

        { key: 'next_mvmt_return_time', tdClass: 'vsm-dt-cell', label:'Next Return', sortable: true, thStyle: { width: "150px" }},
        { key: 'next_mvmt_start_base', label: 'Next Start', sortable: true, thStyle: { width: "160px" }},
        { key: 'next_mvmt_route_name', label: 'Next Route Name', sortable: true, thStyle: { width: "180px" }},
        { key: 'next_mvmt_driver', label:'Next Driver', sortable: true, thStyle: { width: "150px" }},
        { key: 'next_mvmt_id', label:'Next ID', sortable: true, thStyle: { width: "180px" }},
        { key: 'next_mvmt_booking_category', label:'Booking Category', sortable: true, thStyle: { width: "150px" }},
        { key: 'next_mvmt_driver_start', tdClass: 'vsm-dt-cell', label: 'Next Driver Start', sortable: true, visible: true, thStyle: { width: "150px" }},
        { key: 'next_mvmt_time', tdClass: 'vsm-dt-cell', label:'Next Depart', sortable: true, thStyle: { width: "150px" }},
        { key: 'next_mvmt_delta', label:'Departs In', sortable: true, thStyle: { width: "150px" }},
        { key: 'next_mvmt_days', label:'#', sortable: true, visible: true, thStyle: { width: "50px" }},

        { key: 'wheelchair', label: 'WC', sortable: true, thStyle: { width: "75px" }},
        { key: 'stayanddo', label: 'S&D', sortable: true, thStyle: { width: "75px" }},
        { key: 'tote', label: 'Tote', sortable: true, thStyle: { width: "80px" }},
        { key: 'notes', label: 'Notes', sortable: true, thStyle: { width: "250px" }},
      ],
      history_fields: [
        { key: 'processed_time', tdClass: 'vsm-dt-cell', label:'Last Updated', sortable: true},
        { key: 'updated_by', label:'Updated By', sortable: true},
        { key: 'vehicle_status', label:'Status', sortable: true},
        { key: 'maintenance_status', label:'Maint Status', sortable: true},
        { key: 'last_maintenance_time', tdClass: 'vsm-dt-cell', label:'Last Maint Time', sortable: true},
        { key: 'last_cleaned_time', tdClass: 'vsm-dt-cell', label:'Cleaned Time', sortable: true},
        { key: 'last_cleaned_number', label:'Cleaned #', sortable: true},
        { key: 'last_returned_time', tdClass: 'vsm-dt-cell', label:'Returned Time', sortable: true},
        { key: 'wheelchair', label: 'WC', sortable: true},
        { key: 'stayanddo', label: 'S&D', sortable: true},
        { key: 'tote', label: 'Tote', sortable: true},
        { key: 'notes', label: 'Notes', sortable: true},
      ]
    }
  },
  async created() {
    this.fields_all = this.fields
    this.check_features()
  },
  mounted() {
    this.stopTimer()
  },
  beforeDestroy() {
    try { this.flushActiveEdits() } catch(e) { /* best-effort save on teardown */ }
    clearTimeout(this.visibilityReloadTimeout)
    this.stopTimer()
  },
  methods: {
    startTimer() {
      this.stopTimer()
      if (this.reload_mode > 0) {
        this.timer = setInterval(() => this.reload_data(), this.reload_mode)
      }
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    async resetTimerAndReload() {
      this.stopTimer()
      await this.reload_data()
      this.startTimer()
    },

    isMobile() {
      return this.mobileView
    },
    isTablet() {
      if(/iPad/i.test(navigator.userAgent)) {
        return true
      } else {
        return false
      }
    },
    tableSortCompare(itemA, itemB, key) {
      if (key !== 'next_mvmt_delta') {
        return false
      } else {
        return itemA['next_mvmt_ms'] - itemB['next_mvmt_ms']
      }
    },
    async onSpareFocus() {
      this.spares_editing = true
      const atFocus = this.summary.spare_notes || ''
      try {
        // pull the latest value before editing starts, so we're never
        // typing on top of something a background poll hasn't caught up to
        const {value: {data = {}}} = await get_tbn_vehicle_info(this.tenant_id, this.selected_base, this.selected_vehicle_tag, this.mode)
        const fresh = (data.summary || {}).spare_notes || ''
        this.spare_notes_baseline = fresh
        // only apply it if the user hasn't already started typing - this
        // fetch can resolve mid-keystroke and we must not wipe their input
        if ((this.summary.spare_notes || '') === atFocus) {
          this.summary.spare_notes = fresh
        }
      } catch(e) {
        // fall back to whatever's currently on screen
      }
    },
    async save_event_notes() {
      const edited = this.summary.spare_notes || ''
      if (edited === this.spare_notes_baseline) {
        this.spares_editing = false
        return
      }
      try {
        let json = {
          'event_hash': this.summary.spare_notes_hash,
          'notes': edited
        }
        await save_event_notes(this.user.tenant, json)
        this.spare_notes_baseline = edited
      } catch(e) {
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.spares_editing = false
    },
    async check_features() {

      this.mode = ''
      this.driver_mode = false

      if (this.$route.path.indexOf('/vsm-maint-tbn/') == -1) {
        this.standalone = false
        this.tenant_id = this.user.tenant
      }
      else if (this.$route.path.indexOf('/vsm-maint-tbn/reload/') != -1) {

        this.standalone = true
        let filter = this.$route.path.split('/vsm-maint-tbn/reload/')[1]

        if (filter.indexOf('/') != -1) {
          this.tenant_id = filter.split("/")[0];
          this.selected_base = filter.split("/")[1];
        } else {
          this.tenant_id = filter;
        }

        this.reload_mode = 60000
      }
      else if (this.$route.path.indexOf('/vsm-maint-tbn/reloadreturn/') != -1) {
        this.standalone = true
        this.mode = 'return'
        let filter = this.$route.path.split('/vsm-maint-tbn/reloadreturn/')[1]

        if (filter.indexOf('/') != -1) {
          this.tenant_id = filter.split("/")[0];
          this.selected_base = filter.split("/")[1];
        } else {
          this.tenant_id = filter;
        }
        this.reload_mode = 60000
      }
      else if (this.$route.path.indexOf('/vsm-maint-tbn/driver/') != -1) {
        this.standalone = true
        this.driver_mode = true
        let filter = this.$route.path.split('/vsm-maint-tbn/driver/')[1]

        if (filter.indexOf('/') != -1) {
          this.tenant_id = filter.split("/")[0];
          this.selected_base = filter.split("/")[1];
        } else {
          this.tenant_id = filter;
        }
        this.reload_mode = 60000
      }

      else if (this.$route.path.indexOf('/vsm-maint-tbn/') != -1) {
        this.standalone = true
        this.tenant_id = this.$route.path.split('/vsm-maint-tbn/')[1]
      }

      this.fields = this.fields_all
      if (this.standalone)
        this.fields = this.fields.filter(item => (item.key != 'details' && item.key != 'processed_time' && item.key != 'last_out_time' && item.key != 'last_dirty_time' && item.key != 'last_cleaned_time' && item.key != 'last_cleaned_number'))

      if (this.isMobile())
        this.fields = this.fields.filter(item => (item.key == 'name' || item.key == 'vehicle_status' || item.key == 'next_mvmt_delta'))

      this.load_data()
    },
    async update_wc_after(row, item) {
      this.$nextTick(() => {
        this.update_wc(row, item);
      });
    },
    async update_stayanddo_after(row, item) {
      this.$nextTick(() => {
        this.update_stayanddo(row, item);
      });
    },
    async update_tote_after(row, item) {
      this.$nextTick(() => {
        this.update_tote(row, item);
      });
    },

    // picker save hook (vsm_ops_table mixin): optimistic set, then the same
    // endpoint path the old dropdown used
    applyStatusChange(item, field, value) {
      this.$set(item, field, value)
      if (field === 'maintenance_status')
        this.update_maint_status({}, item)
      else
        this.update_vehicle_status({}, item)
    },
    // auto-refresh hook (vsm_ops_table mixin)
    downloadCsv() {
      downloadVsmCsv(this.visibleFields, this.items, 'vsm-' + moment().format('MM-DD-YYYY') + '.csv')
    },
    opsSoftReload() {
      this.reload_data()
    },
    async update_maint_status(row, item) {
      this.updating = true
      try {
        let json = {
            'name': item.name,
            'maintenance_status': item.maintenance_status,
        }

        const {value: { data = [] } } = await tbn_update_vehicle_status(this.tenant_id, json)
        if (row.detailsShowing)
          row.toggleDetails();
      } catch(e) {
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.updating = false
    },
    async update_vehicle_status(row, item) {
      this.updating = true
      try {
        let json = {
          'name': item.name,
          'vehicle_status':  item.vehicle_status,
        }

        const {value: { data = [] } } = await tbn_update_vehicle_status(this.tenant_id, json)
        if (row.detailsShowing)
          row.toggleDetails();
      } catch(e) {
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }

      this.updating = false
    },
    async update_wc(row, item) {
      this.updating = true
      try {
        let json = {
          'name': item.name,
          'wheelchair': item.wheelchair,
        }

        const {value: { data = [] } } = await tbn_update_vehicle_status(this.tenant_id, json)
        if (row.detailsShowing)
          row.toggleDetails();
      } catch(e) {
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }

      this.updating = false
    },
    async update_stayanddo(row, item) {
      this.updating = true
      try {
        let json = {
          'name': item.name,
          'stayanddo': item.stayanddo,
        }

        const {value: { data = [] } } = await tbn_update_vehicle_status(this.tenant_id, json)
        if (row.detailsShowing)
          row.toggleDetails();
      } catch(e) {
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.updating = false
    },
    async update_tote(row, item) {
      this.updating = true
      try {
        let json = {
          'name': item.name,
          'tote': item.tote
        }

        const {value: { data = [] } } = await tbn_update_vehicle_status(this.tenant_id, json)
        if (row.detailsShowing)
          row.toggleDetails();
      } catch(e) {
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.updating = false
    },
    markEditing(item) {
      if (item) this.active_edit_names[item.name] = true
    },
    clearEditing(item) {
      if (item) delete this.active_edit_names[item.name]
    },
    flushActiveEdits() {
      Object.keys(this.active_edit_names).forEach(name => {
        const item = (this.items || []).find(i => i.name === name)
        if (item) this.update_notes({}, item)
      })
    },
    async update_notes(row, item) {
      this.updating = true
      try {
        let json = {
          'name': item.name,
          'notes': item.notes,
        }

        const {value: { data = [] } } = await tbn_update_vehicle_status(this.tenant_id, json)
        this.flashNotesSaved(item.name)
        if (row.detailsShowing)
          row.toggleDetails();
      } catch(e) {
        if (this.notes_editing === item.name)
          this.notes_editing = ''
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.clearEditing(item)
      this.updating = false
    },

    async get_vehicle_info_history(selected_item) {
      const {value: { data = [] } } = await get_vehicle_info_history(this.tenant_id, selected_item.name)
      var history_item = this.items_full.filter(item => item.name == selected_item.name)[0]
      history_item.history = data
      this.$forceUpdate()
    },
    // keeps an in-progress Spares edit on-screen through an auto-refresh
    // (mirrors the notes_editing guard in vsm_board.vue), otherwise
    // captures the freshly-loaded value as the new merge baseline
    syncSpareNotesAfterLoad(inProgressSpareNotes) {
      if (this.spares_editing) {
        this.summary.spare_notes = inProgressSpareNotes
      } else {
        this.spare_notes_baseline = this.summary.spare_notes || ''
      }
    },
    async load_data() {
      this.loading = true
      try {
        var results = await get_tbn_vehicle_info(this.tenant_id, this.selected_base, this.selected_vehicle_tag, this.mode)
        const {value: {data = []}} = results

        // snapshot right before overwriting summary (not before the await above)
        // so a focus that happens while the request is in flight isn't missed
        const inProgressSpareNotes = this.spares_editing ? this.summary.spare_notes : null
        this.summary = data.summary
        this.base_options = data.summary.bases
        moment.tz.setDefault(this.summary.timezone)
        data.results = preserveActiveEdits(this.items_full, data.results, this.active_edit_names, ['notes'])
        this.items = data.results
        this.items_full = data.results
        this.syncSpareNotesAfterLoad(inProgressSpareNotes)

        this.stopTimer()
        if (this.reload_mode > 0) {
          this.startTimer()
        }
      } catch(e){
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.loading = false
    },
    async reload_data() {
      try {
        if (this.updating) return
        if (this.reloading) return
        if (this.$pageVisibility && this.$pageVisibility.hidden) return

        this.reloading = true
        var results = await get_tbn_vehicle_info(this.tenant_id, this.selected_base, this.selected_vehicle_tag, this.mode)
        const {value: {data = []}} = results

        const inProgressSpareNotes = this.spares_editing ? this.summary.spare_notes : null
        this.summary = data.summary
        this.base_options = data.summary.bases
        moment.tz.setDefault(this.summary.timezone)
        data.results = preserveActiveEdits(this.items_full, data.results, this.active_edit_names, ['notes'])
        // merge-by-name so open history rows and a focused notes input
        // survive the background refresh (vsm_ops_table)
        this.items_full = this.vsmMergeRows(this.items_full, data.results)
        this.items = this.items_full
        this.syncSpareNotesAfterLoad(inProgressSpareNotes)
        this.filter_items()
      } catch(e){
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.reloading = false
    },
    filter_by_status(status) {
      if (status == null) {
        this.selected_vehicle_statuses = []
      } else if (this.selected_vehicle_statuses.includes(status)) {
        this.selected_vehicle_statuses = this.selected_vehicle_statuses.filter(s => s !== status)
      } else {
        this.selected_vehicle_statuses = [...this.selected_vehicle_statuses, status]
      }
      this.filter_items()
    },
    filter_by_maint_status(status) {
      if (status == null) {
        this.selected_maintenance_statuses = []
      } else if (this.selected_maintenance_statuses.includes(status)) {
        this.selected_maintenance_statuses = this.selected_maintenance_statuses.filter(s => s !== status)
      } else {
        this.selected_maintenance_statuses = [...this.selected_maintenance_statuses, status]
      }
      this.filter_items()
    },
    filter_items() {
      this.items = this.items_full
      if (this.selected_vehicle_statuses.length > 0) {
        const upper = this.selected_vehicle_statuses.map(s => s.toUpperCase())
        this.items = this.items.filter(item => {
          if (this.selected_vehicle_statuses.includes('available') && item.vehicle_status == 'CLEANED' && item.maintenance_status == 'READY') return true
          return upper.includes(item.vehicle_status)
        })
      }
      if (this.selected_maintenance_statuses.length > 0) {
        const upper = this.selected_maintenance_statuses.map(s => s.toUpperCase())
        this.items = this.items.filter(item => {
          if (this.selected_maintenance_statuses.includes('garage') && item.maintenance_status != 'READY') return true
          return upper.includes(item.maintenance_status)
        })
      }
    },
    filter_results() {
      this.items = this.items_full
      const val = this.items_filter
      if (val != '')
        this.items = this.items.filter(item => (this.contains(item.name,val) || (item.vehicle_info != null &&
            (this.contains(item.vehicle_name,val) || this.contains(item.last_cleaned_number,val)))))
    },
    contains(val1, val2) {
      if (val1 != null) {
        return val1.toLowerCase().indexOf(val2.toLowerCase()) != -1;
      }
      return false;
    },
    display_maint_status(status) {
      return displayMaintStatus(status, this.summary && this.summary.dvir_combined)
    },
    get_row_color(item, type) {
      if (item) {
        if (item.vehicle_status && item.maintenance_status)
          return item.vehicle_status.toLowerCase() + "-" + item.maintenance_status.toLowerCase()
        else if (item.vehicle_status)
          return item.vehicle_status.toLowerCase()
        else if (item.maintenance_status)
          return item.maintenance_status.toLowerCase()
        else
          return "unknown"
      }
    },
    get_history_row_color(item, type) {
      return "hist-unknown"
    },

  },
  computed: {
    rowColorsOff() {
      const v = this.summary && this.summary.row_colors_disabled
      return v === true || String(v).toLowerCase() === 'true'
    },
    ...mapState({
      user: state => state.profile.user
    }),
    standaloneId() {
      return (this.summary && this.summary.vsm_standalone_uuid) || this.tenant_id
    },
    cardFields() {
      // the portal's visible column set (not the curated mobile subset):
      // tenant's visible_fields config when present, else the default table
      // catalog — so the cards carry everything the desktop table shows
      if (this.summary && this.summary.visible_fields) {
        return this.summary.visible_fields.split(',')
          .map(f => f.trim())
          .map(key => this.fields_all.filter(field => field.key === key)[0])
          .filter(Boolean)
      }
      return (this.fields_all || []).filter(field => field.visible)
    },
    visibleFields() {
      var custom_fields = null;

      if (this.summary && this.isMobile() && this.summary.visible_mobile_fields)
        custom_fields = this.summary.visible_mobile_fields;
      else if (this.summary && this.driver_mode && this.summary.visible_driver_fields)
        custom_fields = this.summary.visible_driver_fields;
      else if (this.summary && this.summary.visible_fields)
        custom_fields = this.summary.visible_fields;

      if (this.standalone && !this.summary.visible_update_fields) {
        this.fields_all = this.fields_all.filter(item => (item.key != 'details'))
        this.fields = this.fields.filter(item => (item.key != 'details'))
      }

      if (custom_fields != null) {
        let new_fields = []
        for (let visible_field of custom_fields.split(",").map(f => f.trim())) {
          let found_field = this.fields_all.filter(field => visible_field == field.key)
          if (found_field.length > 0) {
            new_fields.push(found_field[0]);
          }
        }
        if (this.isMobile()) {
          new_fields = new_fields.filter(field => field.key !== 'maintenance_status')
        }
        return new_fields;
      }

      // no field config resolved for this surface: phones used to fall
      // through to the full catalog below. use the curated mobile set.
      if (this.isMobile()) {
        let mobile_fields = []
        for (let key of VSM_MOBILE_DEFAULT_FIELD_KEYS) {
          let found_field = this.fields_all.filter(field => key == field.key)
          if (found_field.length > 0) mobile_fields.push(found_field[0])
        }
        if (mobile_fields.length > 1) return mobile_fields
      }
      let fields = this.fields.filter(field => field.visible)
      if (this.isMobile()) {
        fields = fields.filter(field => field.key !== 'maintenance_status')
      }
      return fields
    },
    visibleHistoryFields() {
      var custom_fields = null;

      if (this.summary && this.summary.visible_history_fields)
        custom_fields = this.summary.visible_history_fields;

      if (custom_fields != null) {
        let new_fields = []
        for (let visible_field of custom_fields.split(",").map(f => f.trim())) {
          let found_field = this.history_fields.filter(field => visible_field == field.key)
          if (found_field.length > 0) {
            new_fields.push(found_field[0]);
          }
        }
        return new_fields;
      } else
        return this.history_fields
    },
    available_cnt() {
      return this.items_full.filter(item => (item.vehicle_status == 'CLEANED' && item.maintenance_status == 'READY')).length
    },
    clean_cnt() {
      return this.items_full.filter(item => item.vehicle_status == 'CLEANED').length
    },
    dirty_cnt() {
      return this.items_full.filter(item => item.vehicle_status == 'DIRTY').length
    },
    out_cnt() {
      return this.items_full.filter(item => item.vehicle_status == 'OUT').length
    },
    garage_cnt() {
      return this.items_full.filter(item => item.maintenance_status != 'READY').length
    },
    ready_cnt() {
      return this.items_full.filter(item => item.maintenance_status == 'READY').length
    },
    oos_cnt() {
      return this.items_full.filter(item => item.maintenance_status == 'OOS').length
    },
    pm_cnt() {
      return this.items_full.filter(item => item.maintenance_status == 'PM').length
    },
  },
  watch: {
    // crossing the phone breakpoint re-derives the field set (check_features
    // trims fields under isMobile(); without this a widened window keeps the
    // 3-column phone set)
    mobileView() {
      this.check_features()
    },
    '$route'() {
      this.stopTimer()
      this.check_features()
    },
    user: {
      deep: true,
      handler() {
        this.tenant_id = this.user.tenant
        this.load_data()
      }
    },
    items_filter() {
      this.filter_results()
    },
    selected_base(val) {
      this.load_data()
    },
    reload_mode(val) {
      this.stopTimer()
      if (val > 0)
        this.startTimer()
    },
    // debounced: some environments (VMs, remote desktop, etc.) fire
    // document visibilitychange rapidly/spuriously rather than on a real
    // tab-switch, which was causing a reload storm that stomped on
    // whatever the user was actively typing before their edit could ever
    // register. Only reload once visibility has actually settled.
    '$pageVisibility.hidden'(newVal, oldVal) {
      clearTimeout(this.visibilityReloadTimeout)
      if (oldVal && !newVal) {
        this.visibilityReloadTimeout = setTimeout(() => {
          if (!this.$pageVisibility.hidden) this.resetTimerAndReload()
        }, 1000)
      }
    }
  }
}
</script>

<style scoped>

.total { border: 1px solid grey; border-radius: 5px; padding: 5px 1px 1px 1px; margin:2px}

.dirty { background-color: hsla(25,28%,23%,0.4); border: 1px solid grey; border-radius: 5px; padding: 5px 1px 1px 1px; margin:2px }
.cleaned { border: 1px solid grey; border-radius: 5px; padding: 5px 1px 1px 1px; margin:2px}
.available { background-color: hsla(120,100%,75%,0.3); border: 1px solid grey; border-radius: 5px; padding: 5px 1px 1px 1px; margin:2px}
.garage { background-color: white; border: 1px solid grey; border-radius: 5px; padding: 5px 1px 1px 1px; margin:2px}
.out { background-color: hsla(290,60%,70%,0.3); border: 1px solid grey; border-radius: 5px; padding: 5px 1px 1px 1px; margin:2px}
.oos { background-color: hsla(357,78%,65%,0.4); border: 1px solid grey; border-radius: 5px; padding: 5px 1px 1px 1px; margin:2px}
.pm { background-color: hsla(210,100%,50%,0.4); border: 1px solid grey; border-radius: 5px; padding: 5px 1px 1px 1px; margin:2px}
.ready { border: 1px solid grey; border-radius: 5px; padding: 5px 1px 1px 1px; margin:2px}

.unknown td { background-color: white}

.cleaned td { background-color: hsla(190,70%,50%,0.4) }
.cleaned-ready td { background-color: hsla(190,70%,50%,0.4); }
.cleaned-oos td { background-color: hsla(190,70%,50%,0.4); box-shadow: inset 0 -2px 0 0 hsl(357, 70%, 55%); }
.cleaned-pm td { background-color: hsla(190,70%,50%,0.4); box-shadow: inset 0 -2px 0 0 hsl(210, 100%, 45%); }

.dirty td { background-color: hsla(25,28%,23%,0.4)}
.dirty-ready td { background-color: hsla(25,28%,23%,0.4); }
.dirty-oos td { background-color: hsla(25,28%,23%,0.4); box-shadow: inset 0 -2px 0 0 hsl(357, 70%, 55%); }
.dirty-pm td { background-color: hsla(25,28%,23%,0.4); box-shadow: inset 0 -2px 0 0 hsl(210, 100%, 45%); }

.out td { background-color: hsla(290,60%,70%,0.3) }
.out-ready td { background-color: hsla(290,60%,70%,0.3); }
.out-oos td { background-color: hsla(290,60%,70%,0.3); box-shadow: inset 0 -2px 0 0 hsl(357, 70%, 55%); }
.out-pm td { background-color: hsla(290,60%,70%,0.3); box-shadow: inset 0 -2px 0 0 hsl(210, 100%, 45%); }

.oos td { background-color: hsla(357,78%,65%,0.4) }
.pm td { background-color: hsla(210,100%,50%,0.4) }

.hist-unknown td { background-color: white !important; background-image: none}

.table td {
  vertical-align: middle !important;
  text-align: center !important;
}

tr:nth-child(even) {
  background-color: white !important;
}
tr:nth-child(odd) {
  background-color: white !important;
}

.table tr {
  padding: .1rem !important;
  vertical-align: middle !important;
  text-align: center !important;
}

.header {
  font-weight: bold;
}

div.col {
  border: 1px solid grey;
}

div.wrap {
  border: 1px solid grey;
  flex-wrap: wrap;
  display: flex;
  inline-size: 150px;
}

</style>

